import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { getMessage } from "@/utils/message";
import { Filter, SortAscending } from "@carbon/icons-react";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getMyGroups } from "../../api/group.api";
import CreateGroupModal from "../../components/CreateGroupModal/CreateGroupModal";
import GroupCard from "../../components/GroupCard/GroupCard";
import SearchModal from "../../components/SearchModal/SearchModal";
import {
  GroupRoleEnum,
  GroupSortEnum,
  GroupTypeEnum,
} from "../../constants/group.constant";
import { GroupPathsEnum } from "../../constants/group.paths";
import { groupActions } from "../../group";
import { GroupDef } from "../../types/group.type";

const GroupsScreen = () => {
  const { toast } = useToast();
  const dispatch = useAppDispatch();
  const loadings = useAppSelector((state) => state.group.loading);
  const location = useLocation();

  const [myGroups, setMyGroups] = useState<GroupDef[]>([]);
  const [rootGroups, setRootGroups] = useState<GroupDef[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(9);
  const [isLoadMore, setIsLoadMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [totalElement, setTotalElement] = useState(0);
  const navigate = useNavigate();

  const flagList = useAppSelector((state) => state.group.flagList);

  const [roleFilter, setRoleFilter] = useState<string | null>(null);
  const [rootGroupId, setRootGroupId] = useState<number | undefined>(undefined);
  const [sort, setSort] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  // Update filter states when URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    setRoleFilter(urlParams.get("roleFilter"));
    setRootGroupId(Number(urlParams.get("rootGroupId")) || undefined);
    setSort(urlParams.get("sort"));
    setTypeFilter(urlParams.get("typeFilter"));
  }, [location.search]);

  let isFiltering = false;
  if (roleFilter || rootGroupId || sort || typeFilter) {
    isFiltering = true;
  }

  const fetchMyGroupsInfiniteScroll = async (pageScroll: number) => {
    try {
      setIsLoadMore(true);
      const result = await getMyGroups({
        pageNumber: pageScroll,
        pageSize: pageSize,
        typeFilter: typeFilter as GroupTypeEnum,
        roleFilter: roleFilter as GroupRoleEnum,
        rootGroupId: rootGroupId,
        sort: sort as GroupSortEnum,
      });

      if (!result.data.length || result.data.length < pageSize) {
        setIsLoadMore(false);
      }
      setMyGroups(() => [...myGroups, ...result.data]);
    } catch (error: any) {
      toast({
        title: "Thất bại!",
        variant: "error",
        description: getMessage(error.response?.data.errors[0]),
      });
    }
  };

  const fetchMyGroups = async (pageNumber: number) => {
    try {
      setLoading(true);
      setIsLoadMore(true);
      const result = await getMyGroups({
        pageNumber: pageNumber,
        pageSize: pageSize,
        typeFilter: typeFilter as GroupTypeEnum,
        roleFilter: roleFilter as GroupRoleEnum,
        rootGroupId: rootGroupId,
        sort: sort as GroupSortEnum,
      });

      if (!result.data.length || result.data.length < pageSize) {
        setIsLoadMore(false);
      }
      setTotalElement(result.totalElements);
      setMyGroups(result.data);
    } catch (error: any) {
      toast({
        title: "Thất bại!",
        variant: "error",
        description: getMessage("MSG12"),
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRootGroups = async () => {
    try {
      const result = await getMyGroups({
        typeFilter: GroupTypeEnum.ROOT,
      });
      setRootGroups(result.data);
    } catch (error: any) {
      toast({
        title: "Thất bại!",
        variant: "error",
        description: getMessage("MSG12"),
      });
    }
  };

  const fetchFlagGroups = async () => {
    try {
      const result = await getMyGroups({});
      dispatch(
        groupActions.setFlagList(result.data.length == 0 ? true : false)
      );
    } catch (error: any) {
      toast({
        title: "Thất bại!",
        variant: "error",
        description: getMessage("MSG12"),
      });
    }
  };

  useEffect(() => {
    setPage(1);
    fetchMyGroups(1);
    fetchRootGroups();
  }, [roleFilter, rootGroupId, sort, typeFilter, loadings]);

  useEffect(() => {
    fetchMyGroups(page);
    fetchFlagGroups();
  }, []);

  return (
    <div className="min-h-screen bg-secondary-25">
      {/* Enhanced Header with Integrated Filters */}
      <div className="bg-white   sticky top-[70px] z-40">
        <div className="container mx-auto px-4 sm:px-6">
          {/* Main Header Row */}
          <div className="py-4 border-b border-secondary-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center space-x-3">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-secondary-900">
                    Nhóm của tôi
                  </h1>
                  <p className="text-sm text-secondary-600 mt-1">
                    Quản lý và tham gia các nhóm của bạn
                  </p>
                </div>
                <Badge
                  variant="secondary"
                  className="bg-primary-50 text-primary-700 px-2 py-1 text-xs font-semibold"
                >
                  {totalElement}
                </Badge>
              </div>
              <div className="flex items-center space-x-3">
                <SearchModal />
                <CreateGroupModal haveSubGroup={true} />
              </div>
            </div>
          </div>

          {/* Quick Filters Row */}
          {!flagList && (
            <div className="py-3">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                {/* Quick Filter Pills */}
                <div className="flex items-center gap-3">
                  <div className="flex items-center space-x-2">
                    <Filter
                      size={16}
                      className="text-secondary-500"
                    />
                    <span className="text-sm font-medium text-secondary-700">
                      Lọc nhanh:
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {/* All Groups Pill */}
                    <button
                      onClick={() => {
                        setRoleFilter(null);
                        setRootGroupId(undefined);
                        setSort(null);
                        setTypeFilter(null);
                        setPage(1);
                        navigate(`${window.location.pathname}`);
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
                        !isFiltering
                          ? "bg-primary-500 text-white border-primary-500 shadow-sm"
                          : "bg-white text-secondary-600 border-secondary-200 hover:border-secondary-300 hover:bg-secondary-50"
                      }`}
                    >
                      Tất cả
                    </button>

                    {/* Manager Groups Pill */}
                    <button
                      onClick={() => {
                        const urlParams = new URLSearchParams(location.search);
                        setRoleFilter(GroupRoleEnum.MANAGER);
                        setTypeFilter(null);
                        setRootGroupId(undefined);
                        urlParams.set("roleFilter", GroupRoleEnum.MANAGER);
                        urlParams.delete("typeFilter");
                        urlParams.delete("rootGroupId");
                        setPage(1);
                        navigate(
                          `${window.location.pathname}?${urlParams.toString()}`
                        );
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
                        roleFilter === GroupRoleEnum.MANAGER
                          ? "bg-orange-500 text-white border-orange-500 shadow-sm"
                          : "bg-white text-secondary-600 border-secondary-200 hover:border-secondary-300 hover:bg-secondary-50"
                      }`}
                    >
                      Nhóm bạn quản lý
                    </button>

                    {/* Organizations Pill */}
                    <button
                      onClick={() => {
                        const urlParams = new URLSearchParams(location.search);
                        setTypeFilter(GroupTypeEnum.ROOT);
                        setRoleFilter(null);
                        setRootGroupId(undefined);
                        urlParams.set("typeFilter", GroupTypeEnum.ROOT);
                        urlParams.delete("roleFilter");
                        urlParams.delete("rootGroupId");
                        setPage(1);
                        navigate(
                          `${window.location.pathname}?${urlParams.toString()}`
                        );
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
                        typeFilter === GroupTypeEnum.ROOT
                          ? "bg-blue-500 text-white border-blue-500 shadow-sm"
                          : "bg-white text-secondary-600 border-secondary-200 hover:border-secondary-300 hover:bg-secondary-50"
                      }`}
                    >
                      Tổ chức
                    </button>

                    {/* Groups Pill */}
                    <button
                      onClick={() => {
                        const urlParams = new URLSearchParams(location.search);
                        setTypeFilter(GroupTypeEnum.GROUP);
                        setRoleFilter(null);
                        setRootGroupId(undefined);
                        urlParams.set("typeFilter", GroupTypeEnum.GROUP);
                        urlParams.delete("roleFilter");
                        urlParams.delete("rootGroupId");
                        setPage(1);
                        navigate(
                          `${window.location.pathname}?${urlParams.toString()}`
                        );
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
                        typeFilter === GroupTypeEnum.GROUP
                          ? "bg-green-500 text-white border-green-500 shadow-sm"
                          : "bg-white text-secondary-600 border-secondary-200 hover:border-secondary-300 hover:bg-secondary-50"
                      }`}
                    >
                      Nhóm con
                    </button>
                  </div>
                </div>

                {/* Advanced Filters */}
                <div className="flex items-center gap-2">
                  {/* Sort Dropdown */}
                  <Select
                    value={sort || "default"}
                    onValueChange={(value) => {
                      const urlParams = new URLSearchParams(location.search);
                      if (value === "default") {
                        setSort(null);
                        urlParams.delete("sort");
                      } else {
                        setSort(value);
                        urlParams.set("sort", value);
                        setPage(1);
                      }
                      navigate(
                        `${window.location.pathname}?${urlParams.toString()}`
                      );
                    }}
                  >
                    <SelectTrigger className="w-auto min-w-[120px] h-8 text-xs border-secondary-200 focus:border-primary-300 hover:border-primary-200 transition-colors bg-white">
                      <SortAscending
                        size={14}
                        className="mr-1 text-secondary-500"
                      />
                      <SelectValue placeholder="Sắp xếp" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="default">Mặc định</SelectItem>
                        <SelectItem value={GroupSortEnum.NAME}>
                          Theo tên
                        </SelectItem>
                        <SelectItem value={GroupSortEnum.UPDATED_DATE}>
                          Mới cập nhật
                        </SelectItem>
                        <SelectItem value={GroupSortEnum.CREATED_DATE}>
                          Mới tạo
                        </SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>

                  {/* Organization Filter (if has root groups) */}
                  {rootGroups.length > 0 && (
                    <Select
                      value={rootGroupId ? String(rootGroupId) : "default"}
                      onValueChange={(value) => {
                        const urlParams = new URLSearchParams(location.search);
                        if (value === "default") {
                          urlParams.delete("rootGroupId");
                          setRootGroupId(undefined);
                        } else {
                          urlParams.set("rootGroupId", value);
                          setRootGroupId(Number(value));
                          setPage(1);
                        }
                        navigate(
                          `${window.location.pathname}?${urlParams.toString()}`
                        );
                      }}
                    >
                      <SelectTrigger className="w-auto min-w-[130px] max-w-[160px] h-8 text-xs border-secondary-200 focus:border-primary-300 hover:border-primary-200 transition-colors bg-white">
                        <SelectValue placeholder="Chọn tổ chức" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="default">
                            Tất cả tổ chức
                          </SelectItem>
                          {rootGroups.map((group, index) => (
                            <SelectItem
                              key={index}
                              value={String(group.groupId)}
                            >
                              <span className="truncate">{group.name}</span>
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  )}

                  {/* Clear Filters */}
                  {isFiltering && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setRoleFilter(null);
                        setRootGroupId(undefined);
                        setSort(null);
                        setTypeFilter(null);
                        setPage(1);
                        navigate(`${window.location.pathname}`);
                      }}
                      className="h-8 px-2 text-xs text-secondary-500 hover:text-secondary-700 hover:bg-secondary-50"
                    >
                      <svg
                        className="w-3 h-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          </div>
        ) : (
          <>
            {/* Filter Loading Indicator */}
            {loading && isFiltering && (
              <div className="flex items-center justify-center py-4 mb-6">
                <div className="flex items-center space-x-2 text-secondary-600">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  <span className="text-sm">Đang áp dụng bộ lọc...</span>
                </div>
              </div>
            )}

            <InfiniteScroll
              style={{
                height: "unset",
                overflow: "unset",
              }}
              dataLength={myGroups.length}
              next={() => {
                setPage((prev) => prev + 1);
                fetchMyGroupsInfiniteScroll(page + 1);
              }}
              hasMore={isLoadMore}
              loader={
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                </div>
              }
            >
              {myGroups.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="max-w-md">
                    <h3 className="text-xl font-semibold text-secondary-900 mt-6 mb-3">
                      {isFiltering
                        ? "Không tìm thấy nhóm nào"
                        : "Chưa có nhóm nào"}
                    </h3>
                    <p className="text-secondary-600 mb-8 leading-relaxed">
                      {isFiltering
                        ? "Thử điều chỉnh bộ lọc để tìm thấy nhóm phù hợp với bạn"
                        : "Bạn chưa là thành viên của nhóm nào. Hãy tạo nhóm mới hoặc khám phá các nhóm hiện có."}
                    </p>

                    {!isFiltering && (
                      <div className="flex justify-center space-x-4">
                        <CreateGroupModal haveSubGroup={true} />
                        <Link
                          to={GroupPathsEnum.GROUP_DISCOVER}
                          className="inline-flex items-center px-6 py-3 text-sm font-medium border border-primary-600 rounded-lg text-primary-600 hover:bg-primary-50 transition-colors duration-200"
                        >
                          Khám phá nhóm
                        </Link>
                      </div>
                    )}

                    {/* Show clear filter button in empty state when filtering */}
                    {isFiltering && (
                      <div className="flex justify-center mt-4">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setRoleFilter(null);
                            setRootGroupId(undefined);
                            setSort(null);
                            setTypeFilter(null);
                            setPage(1);
                            navigate(`${window.location.pathname}`);
                          }}
                          className="text-primary-600 border-primary-600 hover:bg-primary-50"
                        >
                          Xóa tất cả bộ lọc
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {myGroups.map((group, index) => (
                    <GroupCard
                      key={index}
                      group={group}
                    />
                  ))}
                </div>
              )}
            </InfiniteScroll>
          </>
        )}
      </div>
    </div>
  );
};

export default GroupsScreen;
